import { Table } from '@tanstack/react-table';
import { ArrowLeft, ArrowLeftToLine, ArrowRight, ArrowRightToLine } from 'lucide-react';

import { Button } from '@/components/button';
import { Text } from '@/components/text';
import { Select } from '@/components/ui/inputs/select';
import { tv } from '@/utils/styles';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  className?: string;
}

const DataTablePagination = <TData,>({ table, className }: DataTablePaginationProps<TData>) => {
  const { base, col, pageButton, pageCount, buttons } = styles();

  return (
    <div className={base({ className })}>
      <div className={col()}>
        <Text variant="secondary" className="mb-0 shrink-0">
          Rows per page
        </Text>
        <Select
          id="rows-per-page"
          size="small"
          defaultSelectedItem={`${table.getState().pagination.pageSize}`}
          onSelect={(selectedOption) => {
            table.setPageSize(Number(selectedOption.value));
          }}
          options={[
            {
              items: [20, 30, 40, 50].map((pageSize) => ({
                label: `${pageSize}`,
                value: `${pageSize}`,
              })),
            },
          ]}
          side="top"
          position="popper"
          avoidCollisions
        />
      </div>
      <div className={col()}>
        <div className={pageCount()}>
          <Text variant="secondary" className="mb-0">
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </Text>
        </div>
        <div className={buttons()}>
          <Button
            variant="outline"
            className={pageButton({ className: 'hidden lg:flex' })}
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to first page</span>
            <ArrowLeftToLine size={16} />
          </Button>
          <Button
            variant="outline"
            className={pageButton()}
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to previous page</span>
            <ArrowLeft size={16} />
          </Button>
          <Button
            variant="outline"
            className={pageButton()}
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to next page</span>
            <ArrowRight size={16} />
          </Button>
          <Button
            variant="outline"
            className={pageButton({ className: 'hidden lg:flex' })}
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to last page</span>
            <ArrowRightToLine size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'flex items-center justify-between space-x-6 border-t border-grey-200 bg-white px-6 py-3 text-sm lg:space-x-8',
    col: 'flex items-center space-x-2',
    pageButton: 'h-8 w-8 p-0',
    pageCount: 'mr-4',
    buttons: 'flex items-center gap-2',
  },
});

export { DataTablePagination };
